import { createContext, useContext } from 'react';

/**
 * The confirmation context and its hook, split from the provider component
 * the same way `rtcSessionContext` is split from `RtcSessionProvider`: a
 * module that exports both a component and a hook cannot be hot-replaced on
 * its own, so editing any control in the UI barrel would reload the app.
 */

export interface ConfirmRequest {
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
}

export type ConfirmFn = (request: ConfirmRequest) => Promise<boolean>;

export const ConfirmContext = createContext<ConfirmFn | null>(null);

/**
 * Replaces `window.confirm` for destructive actions: the same await-a-boolean
 * shape, but themed, dismissable with Escape, and legible on mobile.
 *
 *     if (!(await confirm({ title: 'Delete this task?', destructive: true }))) return;
 */
export function useConfirm(): ConfirmFn {
  const fn = useContext(ConfirmContext);
  if (!fn) throw new Error('useConfirm must be used inside <ConfirmProvider>');
  return fn;
}
